// Markdown postmortem report writer. Writes to ~/.postmortem/reports/ with a
// filesystem-safe filename (NO colons — invalid on Windows). Format follows
// spec §15.

import { mkdirSync, writeFileSync } from "node:fs";
import { join } from "node:path";
import type { Incident } from "./types.js";

/** `2026-06-23-143301.md` — date + local HHMMSS, colon-free and Windows-safe. */
export function reportFilename(detectedAtIso: string): string {
  const d = new Date(detectedAtIso);
  const p = (n: number) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  const time = `${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  return `${date}-${time}.md`;
}

export function renderReport(incident: Incident): string {
  const d = new Date(incident.detectedAt);
  const p = (n: number) => String(n).padStart(2, "0");
  const when = Number.isNaN(d.getTime())
    ? incident.detectedAt
    : `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  const lines: string[] = [];

  lines.push(`# ☠ Incident Report · ${when}`, "");
  lines.push(`**Severity:** ${incident.severity.toUpperCase()}  `);
  if (incident.brainLabel) lines.push(`**Brain:** ${incident.brainLabel}  `);
  lines.push("", "---", "");

  lines.push("## Summary", incident.title, "");

  if (incident.rootCause) {
    const conf = incident.confidence ? ` _(confidence: ${incident.confidence})_` : "";
    lines.push(`## Likely Cause${conf}`, incident.rootCause, "");
  }

  if (incident.timeline.length > 0) {
    lines.push("## Timeline", "", "| Time | Event | Source |", "|------|-------|--------|");
    for (const t of incident.timeline) {
      lines.push(`| ${t.time} | ${t.text} | ${t.source} |`);
    }
    lines.push("");
  }

  if (incident.suggestedAction) {
    lines.push("## Suggested Action", incident.suggestedAction, "");
  }

  if (incident.patternMatch) {
    lines.push("## Pattern Match", incident.patternMatch, "");
  }

  lines.push("---", "*Generated by postmortem ☠*", "");
  return lines.join("\n");
}

/** Render and write the report. Returns the file path. */
export function writeReport(dir: string, incident: Incident): string {
  mkdirSync(dir, { recursive: true });
  const path = join(dir, reportFilename(incident.detectedAt));
  writeFileSync(path, renderReport(incident), "utf8");
  return path;
}
